import React, { ComponentProps, useMemo, useState } from "react";
import styled from "styled-components";

import useRouterBasename from "../../hooks/useRouterBasename";
import { normalizeHref } from "../../utils/normalizeHref";
import { IconNewWindowLine } from "../icons";
import VSpace from "../layout/VSpace";

import { Link, RouterLink } from "./Link";

type Props = ComponentProps<typeof Link>;

export const ExternalLink = styled(Link)`
  &::after {
    content: "";
    display: inline-block;
    position: relative;
    width: 1.1em;
    height: 1.1em;
    top: 0.2em;
    margin-left: 0.2em;

    background-image: url(${IconNewWindowLine.DataUrl});
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
  }
`;

export const MatterportLinkWithEmbed = (props: {
  href?: string;
  title?: string;
  className?: string;
  style?: React.CSSProperties;
}) => {
  return (
    <VSpace>
      <div
        style={{
          left: 0,
          position: "relative",
          overflow: "hidden",
          width: "100%",
          height: 0,
          paddingBottom: "75%", // 4:3 aspect ratio
        }}
      >
        <iframe
          title={props.title ?? "Matterport Tour"}
          src={props.href}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            border: 0,
          }}
          allowFullScreen
          allow="xr-spatial-tracking *; fullscreen *;"
        ></iframe>
      </div>
      <ExternalLink target="_blank" {...props}></ExternalLink>
    </VSpace>
  );
};

const ContentLink = ({ href, ...props }: Props) => {
  const [actualHref, setActualHref] = useState(href);
  const routerBasename = useRouterBasename();
  const applicationHref = `
    ${window.location.protocol}//${window.location.host}${
      routerBasename ?? "/"
    }`;

  const { isExternal, isRelative, normalized } = useMemo(() => {
    const result = normalizeHref(
      actualHref,
      applicationHref,
      process.env.REACT_APP_REST_API_URL,
    );

    let actual: URL;
    // set the referer query parameter to the current page
    // in case the user turned off the referer header
    try {
      actual = new URL(actualHref);
    } catch {
      return result;
    }

    actual.searchParams.set("referer", window.location.href.split("?")[0]);

    if (result.isPermalink) {
      fetch(actual.toString(), {
        headers: {
          Accept: "application/json",
        },
      })
        .then(async (response) => {
          const bodyText = await response.text();
          const body: { location?: string } | undefined =
            await JSON.parse(bodyText);
          if (body?.location != null) {
            setActualHref(body.location);
          } else {
            console.warn(
              "Unparseable response returned from permalink API",
              bodyText,
            );
          }
        })
        .catch((e) => {
          console.warn("Unable to fetch from permalink API", e);
        });
    }

    return result;
  }, [actualHref, applicationHref]);

  const hasRouter = routerBasename != null;

  if (isExternal) {
    if (normalized.match(/matterport\.com/)) {
      return (
        <MatterportLinkWithEmbed
          href={normalized}
          // target="_blank"
          // rel="noopener noreferer"
          {...props}
          title={`${props.title ?? ""} (opens in new tab)`}
        />
      );
    }

    return (
      <ExternalLink
        href={normalized}
        target="_blank"
        rel="noopener noreferer"
        {...props}
        title={`${props.title ?? ""} (opens in new tab)`}
      />
    );
  }

  if (hasRouter && isRelative) {
    return <RouterLink to={normalized} {...props} />;
  }

  return <Link href={normalized} {...props} />;
};

ContentLink.External = ExternalLink;

export default ContentLink;
